<template>
    <DCard
        padding
    >
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol
                sm="3"
                class="pt-1"
            >
                <DInput
                    id="search"
                    v-model="filterParams.search"
                    :label="__('messages.search')"
                    class="mb-0"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="is_eu-dropdown"
                    v-model="filterParams.is_eu"
                    :label="__('settings.countries.form.is_eu')"
                    :options="BoolDropdown"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="blocked-dropdown"
                    v-model="filterParams.blocked"
                    :label="__('settings.countries.form.blocked')"
                    :options="BoolDropdown"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="filterCountries"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            :items="countries"
            class="table-sticky-header"
            :fields="[
                {key: 'country_code', label: __('settings.countries.table.headers.country_code')},
                {key: 'name_de', label: __('settings.countries.table.headers.name_de')},
                {key: 'name_en', label: __('settings.countries.table.headers.name_en')},
                {key: 'name_da', label: __('settings.countries.table.headers.name_da')},
                {key: 'name_nl', label: __('settings.countries.table.headers.name_nl')},
                {key: 'name_fi', label: __('settings.countries.table.headers.name_fi')},
                {key: 'name_fr', label: __('settings.countries.table.headers.name_fr')},
                {key: 'name_it', label: __('settings.countries.table.headers.name_it')},
                {key: 'name_no', label: __('settings.countries.table.headers.name_no')},
                {key: 'name_pr', label: __('settings.countries.table.headers.name_pr')},
                {key: 'name_es', label: __('settings.countries.table.headers.name_es')},
                {key: 'name_sv', label: __('settings.countries.table.headers.name_sv')},
                {key: 'name_sk', label: __('settings.countries.table.headers.name_sk')},
                {key: 'name_cz', label: __('settings.countries.table.headers.name_cz')},
                {key: 'name_rs', label: __('settings.countries.table.headers.name_rs')},
                {key: 'is_eu', label: __('settings.countries.table.headers.is_eu'), filter: true},
                {key: 'blocked', label: __('settings.countries.table.headers.blocked'), filter: true},
                {key: 'state_required', label: __('settings.countries.table.headers.state_required')},
                {key: 'local_currency_hint_code', label: __('settings.countries.table.headers.local_currency_hint_code')},
                {key: 'actions', label: '', sorter: false, _props: {class: 'end-0'}}
            ]"
            hover
            sorter
        >
            <template #local_currency_hint_code="{item}">
                <td>{{ item.local_currency_hint_enabled ? item.local_currency_hint_code : '-' }}</td>
            </template>

            <template #actions="{item}">
                <td class="position-sticky end-0">
                    <div style="display: flex; grid-gap: 10px;">
                        <DButton
                            :id="`view_${item.id}`"
                            icon="eye"
                            mini
                            @click="$inertia.get(showCountryObject(item.country_code))"
                        />
                        <DButton
                            :id="`edit_${item.id}`"
                            icon="pencil"
                            mini
                            @click="$inertia.get(editCountryObject(item.country_code))"
                        />
                        <DButton
                            :id="`btn-delete-${item.id}`"
                            icon="trash-can-outline"
                            color="danger"
                            mini
                            :disabled="isInLoadingState || item.used_anywhere === 1"
                            @click="openDeleteModal(item.country_code)"
                        />
                    </div>
                </td>
            </template>
        </DTable>

        <DFabs>
            <DFab
                id="btn-add"
                icon="plus"
                :label="__('settings.countries.add_country_button')"
                @click="addCountry"
            />
        </DFabs>

        <DModal
            v-model:show="showDeleteModal"
            :title="__('settings.countries.delete_confirm_modal.title')"
            :message="__('settings.countries.delete_confirm_modal.message')"
            is-confirm
            is-error
            @close="closeDeleteModal"
            @confirm="deleteCountryObject"
        />
    </DCard>
</template>

<script>
import App from '~@/App.vue'
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import LovLayout from '~@/Pages/Settings/Lov/LovLayout.vue'

export default {
    layout: [App, LovLayout]
}
</script>

<script setup>

const props = defineProps({
    countries: {
        type: Array,
        default: () => []
    },
    filters: {
        type: Object,
        default: () => ({})
    }
})

const filterParams = {
    search: props.filters.search,
    is_eu: props.filters.is_eu,
    blocked: props.filters.blocked
}

const BoolDropdown = [
    { value: '', label: '' },
    { value: 1, label: 'true' },
    { value: 0, label: 'false' }
]

const showDeleteModal = ref(false)
const selectedCountry = ref(null)

function openDeleteModal (countryCode) {
    showDeleteModal.value = true
    selectedCountry.value = countryCode
}

function closeDeleteModal () {
    showDeleteModal.value = false
    selectedCountry.value = null
}

const showCountryObject = (countryCode) => route('settings.countries.show', { country: countryCode })
const editCountryObject = (countryCode) => route('settings.countries.edit', { country: countryCode })

const deleteCountryObject = () => router.delete(route('settings.countries.destroy', { country: selectedCountry.value }))

const addCountry = () => router.get(route('settings.countries.create'))

const filterCountries = () => router.get(route('settings.countries.index', {
    save_filters: true
}), filterParams)

const resetFilters = () => router.get(route('settings.countries.index', {
    save_filters: true,
    default_filters: true
}))

</script>
